import { V2_ENDPOINT } from "@/consts";
import { authenticatedFetch } from "@/lib/authenticatedFetch";
import { fetchSlackUsers, type SlackUser } from "../../../form-fields/slackUsers";
import type { FallbackDataFetcher, FallbackFetchContext } from "../types";

/**
 * Module-level TTL cache for Slack workspace user listings. The fallback runs
 * outside React so it can't use TanStack Query — without this, repeated
 * "Refresh" clicks each fire up to MAX_PAGES pages of `users.list`. Keyed by
 * connectorId; invalidated after 5 minutes to match the useSlackUsers hook.
 */
const USER_CACHE_TTL_MS = 5 * 60 * 1000;
const slackUsersCache = new Map<string, { expires: number; users: SlackUser[] }>();

async function fetchSlackUsersCached(connectorId: string): Promise<SlackUser[]> {
  const now = Date.now();
  const cached = slackUsersCache.get(connectorId);
  if (cached && cached.expires > now) return cached.users;

  const users = await fetchSlackUsers(connectorId, "slack");
  slackUsersCache.set(connectorId, { expires: now + USER_CACHE_TTL_MS, users });
  return users;
}

interface SlackSearchMatch {
  type: string;
  text: string;
  user: string;
  username: string;
  ts: string;
  channel?: { id: string; name: string };
}

/**
 * Fetch recent Slack messages that mention the configured user.
 * Used as a fallback for slack-new-user-mention trigger.
 *
 * Strategy:
 * - Read the configured user ID from the trigger payload
 * - Search for messages containing `<@USER_ID>` via slack-find-message
 * - Filter by configured channels (if any)
 * - Transform each match into a trigger result matching the resultSchema
 *
 * Requires: Slack connector with user token (search:read scope).
 */
export const slackUserMentionFallback: FallbackDataFetcher = async (ctx: FallbackFetchContext) => {
  const { triggerPayload, connectorId } = ctx;

  if (!connectorId) return [];

  // The trigger requires a user ID to watch for mentions.
  const userId = triggerPayload.user as string | undefined;
  if (!userId) return [];

  // Build search query: <@USER_ID> matches the mrkdwn format Slack stores
  // in message text when a user is mentioned.
  const configuredChannels = triggerPayload.conversations as string[] | undefined;
  let query = `<@${userId}>`;
  // Slack search `in:` modifiers are AND — multiple `in:` returns zero results.
  // Only apply for exactly one channel; for multiple, rely on post-fetch filtering.
  if (configuredChannels && configuredChannels.length === 1) {
    query += ` in:<#${configuredChannels[0]}>`;
  }

  const response = await authenticatedFetch<{
    data: {
      result: {
        success: boolean;
        data: {
          ret: {
            ok: boolean;
            messages: {
              matches: SlackSearchMatch[];
            };
          };
        };
      };
    };
  }>(`${V2_ENDPOINT}/v1/actions/slack-find-message/test`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      inputs: {
        query,
        count: 10,
        sort: "timestamp",
        sort_dir: "desc",
      },
      connector: { id: connectorId, type: "slack" },
    }),
  });

  if (!response.ok) return [];
  const json = await response.json();

  const allMessages = (json.data?.result?.data?.ret?.messages?.matches ?? []) as SlackSearchMatch[];
  if (allMessages.length === 0) return [];

  // Filter by configured channels if set.
  const channelFilter = configuredChannels && configuredChannels.length > 0 ? new Set(configuredChannels) : null;
  const messages = channelFilter
    ? allMessages.filter(msg => msg.channel?.id && channelFilter.has(msg.channel.id))
    : allMessages;
  if (messages.length === 0) return [];

  // Enrich with avatars from the workspace user list. Uses a module-level
  // 5-minute TTL cache so repeated Refresh clicks don't re-paginate users.list.
  // Swallow errors — avatars are decorative.
  const userAvatarById = new Map<string, string | undefined>();
  const userDisplayById = new Map<string, string>();
  try {
    const users = await fetchSlackUsersCached(connectorId);
    for (const u of users) {
      const avatar = u.profile?.image_48 || u.profile?.image_32 || u.profile?.image_24;
      if (avatar) userAvatarById.set(u.id, avatar);
      const display = u.profile?.display_name || u.real_name || u.name;
      if (display) userDisplayById.set(u.id, display);
    }
  } catch {
    // Non-fatal — rows still render, just without avatars.
  }

  // Transform to match the slack-new-user-mention resultSchema.
  return messages.map(msg => ({
    triggeredAt: new Date(parseFloat(msg.ts) * 1000).toISOString(),
    eventType: "message",
    channel: msg.channel?.id ?? "",
    user: msg.user,
    text: msg.text,
    ts: msg.ts,
    // Metadata for HistoricalPicker MentionRow display.
    // Humanize Slack mrkdwn user mentions: <@U0855RQ3YQN|Mike> → @Mike
    _messagePreview: humanizeSlackMentions(msg.text ?? "").slice(0, 120),
    _userId: msg.user,
    _userName: userDisplayById.get(msg.user) || msg.username || msg.user,
    _avatarUrl: userAvatarById.get(msg.user) ?? "",
    _channelName: msg.channel?.name ?? "",
  }));
};

/**
 * Replace Slack mrkdwn formatting with human-readable text.
 *   <@U0855RQ3YQN|Mike>          → @Mike
 *   <@U0855RQ3YQN>               → @U0855RQ3YQN
 *   <#C01234|general>            → #general
 *   <https://example.com|label>  → label
 *   <https://example.com>        → https://example.com
 *   <!here>, <!channel>, <!everyone> → @here, @channel, @everyone
 */
function humanizeSlackMentions(text: string): string {
  return text
    .replace(/<@([^|>]+)\|([^>]+)>/g, "@$2")
    .replace(/<@([^>]+)>/g, "@$1")
    .replace(/<#([^|>]+)\|([^>]+)>/g, "#$2")
    .replace(/<#([^>]+)>/g, "#$1")
    .replace(/<(https?:\/\/[^|>]+)\|([^>]+)>/g, "$2")
    .replace(/<(https?:\/\/[^>]+)>/g, "$1")
    .replace(/<!here>/g, "@here")
    .replace(/<!channel>/g, "@channel")
    .replace(/<!everyone>/g, "@everyone");
}
